/**
 * Label + control + policy hint. Prototype: docs/HRMS.jsx line 840-848.
 *
 * The prototype's label was a floating `<label>` with no `for`, which reads as
 * unlabelled to a screen reader and doesn't focus its input on click. Field now mints
 * an id and publishes it on a context; TextInput, Select, SearchBox and textareas
 * pick it up automatically. Zero visual change, correct semantics.
 */
import { createContext, useContext, useId, type ReactNode } from "react";
import "./ui.css";

interface FieldControl {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

const FieldContext = createContext<FieldControl | null>(null);

/** Used by the input primitives; returns null outside a Field. */
export function useFieldControl(): FieldControl | null {
  return useContext(FieldContext);
}

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  /** Policy text under the control ("Employees can update this themselves"). */
  hint?: ReactNode;
  /** Validation message. Replaces nothing — it renders under the hint, in red. */
  error?: ReactNode;
  className?: string;
  /** Overrides the generated control id when the caller already has one. */
  htmlFor?: string;
}

export function Field({ label, children, hint, error, className, htmlFor }: FieldProps) {
  const generatedId = useId();
  const id = htmlFor ?? generatedId;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={["ui-field", className].filter(Boolean).join(" ")}>
        <label className="ui-field__label" htmlFor={id}>
          {label}
        </label>
        {children}
        {error ? (
          <span className="ui-field__error" id={errorId} role="alert">
            {error}
          </span>
        ) : null}
        {hint ? (
          <span className="ui-field__hint" id={hintId}>
            {hint}
          </span>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export default Field;
